<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageheader :title="title" :title1="title1" />

        <div class="card">
          <div class="card-body">
            <div v-if="loading" class="details-state">Loading item details...</div>
            <div v-else-if="!item" class="details-state">Item not found.</div>
            <div v-else>
              <div class="details-header">
                <div>
                  <span class="item-code" v-if="item.itemcode">{{ item.itemcode }}</span>
                  <h3>{{ item.itemname }}</h3>
                  <p>{{ item.itemdescription || "No description added." }}</p>
                </div>
                <div class="details-actions">
                  <router-link to="/productlist" class="btn btn-cancel">Back</router-link>
                  <button type="button" class="btn btn-submit" @click="editProduct">Edit Item</button>
                </div>
              </div>

              <div class="row g-3">
                <div class="col-lg-4 col-sm-6 col-12">
                  <div class="detail-box">
                    <span>Category</span>
                    <strong>{{ item.itemcategory || "-" }}</strong>
                  </div>
                </div>
                <div class="col-lg-4 col-sm-6 col-12">
                  <div class="detail-box">
                    <span>Available Stock</span>
                    <strong :class="stockClass">{{ formatNum(item.available) }} {{ item.unit }}</strong>
                  </div>
                </div>
                <div class="col-lg-4 col-sm-6 col-12">
                  <div class="detail-box">
                    <span>Selling Price</span>
                    <strong>Rs. {{ formatNum(item.unitprice) }}</strong>
                  </div>
                </div>
              </div>

              <div class="section-title">Pricing</div>
              <div class="details-grid">
                <div v-for="row in pricingRows" :key="row.label" class="detail-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.value || "-" }}</strong>
                </div>
              </div>

              <div class="section-title">Stock</div>
              <div class="details-grid">
                <div v-for="row in stockRows" :key="row.label" class="detail-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.value || "-" }}</strong>
                </div>
              </div>

              <div class="section-title">Tax</div>
              <div class="details-grid">
                <div v-for="row in taxRows" :key="row.label" class="detail-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.value || "-" }}</strong>
                </div>
              </div>

              <div class="section-title">Unit Of Measurement</div>
              <div class="details-grid">
                <div v-for="row in uomRows" :key="row.label" class="detail-row">
                  <span>{{ row.label }}</span>
                  <strong>{{ row.value || "-" }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

export default {
  name: "product-details",
  data() {
    return {
      title: "Item Details",
      title1: "Full details of an item",
      item: null,
      loading: false,
    };
  },
  computed: {
    stockClass() {
      if (!this.item) return "";
      const available = parseFloat(this.item.available) || 0;
      const threshold = parseFloat(this.item.minstock ?? this.item.threshold) || 0;
      if (available <= 0) return "stock-out";
      if (available <= threshold) return "stock-low";
      return "stock-ok";
    },
    pricingRows() {
      if (!this.item) return [];
      return [
        { label: "Purchase Rate", value: this.money(this.item.purchaserate) },
        { label: "MRP", value: this.money(this.item.mrp) },
        { label: "Selling Price", value: this.money(this.item.unitprice) },
        { label: "Wholesale Rate", value: this.money(this.item.wholesalerate) },
      ];
    },
    stockRows() {
      if (!this.item) return [];
      return [
        { label: "Min Stock", value: this.formatNum(this.item.minstock ?? this.item.threshold) },
        { label: "Rack No", value: this.item.rackno },
        { label: "Barcode", value: this.item.barcode },
        { label: "Unit", value: this.item.unit },
      ];
    },
    taxRows() {
      if (!this.item) return [];
      return [
        { label: "HSN Code", value: this.item.hsncode },
        { label: "Tax Type", value: this.item.taxtype || "GST" },
        { label: "GST %", value: this.percent(this.item.taxpercent) },
        { label: "CGST %", value: this.percent(this.item.cgstrate) },
        { label: "SGST %", value: this.percent(this.item.sgstrate) },
        { label: "IGST %", value: this.percent(this.item.igstrate) },
        { label: "Tax Inclusive", value: this.item.istaxinclusive ? "Yes" : "No" },
      ];
    },
    uomRows() {
      if (!this.item) return [];
      return [
        { label: "Upper Unit", value: this.item.uom_upper },
        { label: "Lower Unit", value: this.item.uom_lower },
        { label: "Conversion Factor", value: this.formatNum(this.item.conversionfactor) },
        { label: "Created At", value: this.formatDate(this.item.createdat) },
      ];
    },
  },
  methods: {
    async getItemDetails() {
      const itemId = this.$route.query.id;
      if (!itemId) {
        this.item = null;
        return;
      }

      this.loading = true;
      try {
        const response = await axios.get(`/v1/items/${itemId}`);
        this.item = response.data && response.data.itemid ? response.data : null;
      } catch (error) {
        console.error("Error fetching item details:", error);
        this.item = null;
        Swal.fire({
          title: "Failed to load item",
          text: error.response?.data?.detail || error.message,
          icon: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    editProduct() {
      if (!this.item) return;
      this.$router.push({ path: "/editproduct", query: { id: this.item.itemid } });
    },
    formatNum(value) {
      if (value === null || value === undefined || value === "") return "";
      return parseFloat(value).toLocaleString("en-IN", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    money(value) {
      const formatted = this.formatNum(value);
      return formatted ? `Rs. ${formatted}` : "";
    },
    percent(value) {
      const formatted = this.formatNum(value);
      return formatted ? `${formatted}%` : "";
    },
    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleString("en-IN");
    },
  },
  beforeMount() {
    this.getItemDetails();
  },
};
</script>

<style scoped>
.details-state {
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  font-size: 14px;
}
.details-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 22px;
  border-bottom: 1px solid #f0f1f5;
  padding-bottom: 18px;
}
.details-header h3 {
  margin: 6px 0;
  font-size: 24px;
  color: #1a1a2e;
}
.details-header p {
  margin: 0;
  color: #6c757d;
}
.item-code {
  display: inline-flex;
  color: #3d5af1;
  background: #f0f4ff;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}
.details-actions {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.detail-box {
  border: 1px solid #edf0f5;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  min-height: 84px;
}
.detail-box span,
.detail-row span {
  display: block;
  font-size: 12px;
  color: #8e92a3;
  margin-bottom: 6px;
}
.detail-box strong,
.detail-row strong {
  color: #1a1a2e;
  font-size: 15px;
  overflow-wrap: anywhere;
}
.section-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 26px 0 12px;
}
.details-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border: 1px solid #edf0f5;
  border-radius: 8px;
  overflow: hidden;
}
.detail-row {
  padding: 14px;
  border-right: 1px solid #edf0f5;
  border-bottom: 1px solid #edf0f5;
  min-height: 72px;
}
.detail-row:nth-child(4n) {
  border-right: 0;
}
.stock-ok { color: #2e7d32 !important; }
.stock-low { color: #e65100 !important; }
.stock-out { color: #c62828 !important; }

@media (max-width: 992px) {
  .details-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .detail-row:nth-child(4n) {
    border-right: 1px solid #edf0f5;
  }
  .detail-row:nth-child(2n) {
    border-right: 0;
  }
}

@media (max-width: 576px) {
  .details-grid {
    grid-template-columns: 1fr;
  }
  .detail-row,
  .detail-row:nth-child(2n),
  .detail-row:nth-child(4n) {
    border-right: 0;
  }
  .details-actions {
    width: 100%;
  }
}
</style>
